import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Search, 
  Globe, 
  Loader2, 
  CheckCircle, 
  ArrowRight,
  Sparkles,
  Languages,
  Tag,
  TrendingUp,
  Edit3,
  AlertCircle,
  ExternalLink,
  Zap,
  Star,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout, { useProjectSteps } from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, StatusChip, SectionHeader } from '@/components/ios26/IOSComponents';
import ThemeValidationEditor from '@/components/ios26/ThemeValidationEditor';
import '@/styles/ios26-design-system.css';
// iter 241 Lot 4 — engine-aware overlays. The rest of the legacy
// screen is preserved so users on the primary engine see no change.
import EngineSelector from '@/components/EngineSelector';
import EngineSeoPanel from '@/components/EngineSeoPanel';

/**
 * Channel Logos for recommendations
 */
const ChannelLogos = {
  pinterest: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  brevo: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
    </svg>
  ),
  x_twitter: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  telegram: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  )
};

/**
 * Channel Recommendations Component v2.0
 * ======================================
 * Features:
 * - Explainable scoring with "Pourquoi ce score?" button
 * - Score breakdown visualization
 * - Analysis summary section
 * - Reset recommendations button
 */
function ChannelRecommendations({ recommendations, projectId, onApply, analysisSummary, recommendationDebug }) {
  const [applying, setApplying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedChannel, setExpandedChannel] = useState(null);
  const navigate = useNavigate();
  
  if (!recommendations || recommendations.length === 0) {
    return null;
  }
  
  const handleApplyRecommendations = async () => {
    setApplying(true);
    try {
      await apiClient.post(`/projects/${projectId}/apply-channel-recommendations`);
      toast.success('Recommandations appliquees ! Les canaux seront tries par pertinence.');
      onApply?.();
    } catch (error) {
      toast.error('Erreur lors de l\'application des recommandations');
    } finally {
      setApplying(false);
    }
  };
  
  const handleResetOrder = async () => {
    setResetting(true);
    try {
      await apiClient.post(`/projects/${projectId}/reset-channel-order`);
      toast.success('Ordre des canaux reinitialise');
      onApply?.();
    } catch (error) {
      toast.error('Erreur lors de la reinitialisation');
    } finally {
      setResetting(false);
    }
  };
  
  const toggleExpanded = (channelId) => {
    setExpandedChannel(expandedChannel === channelId ? null : channelId);
  };
  
  const getBadgeStyle = (level) => {
    switch (level) {
      case 'high':
        return {
          backgroundColor: 'hsl(var(--color-success-bg))',
          color: 'hsl(var(--color-success-text))',
          border: '1px solid hsl(var(--color-success) / 0.3)'
        };
      case 'medium':
        return {
          backgroundColor: 'hsl(var(--color-info-bg))',
          color: 'hsl(var(--color-primary))',
          border: '1px solid hsl(var(--color-primary) / 0.3)'
        };
      default:
        return {
          backgroundColor: 'hsl(var(--color-surface-3))',
          color: 'hsl(var(--color-text-secondary))',
          border: '1px solid hsl(var(--color-border-subtle))'
        };
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 75) return 'hsl(var(--color-success))';
    if (score >= 55) return 'hsl(var(--color-primary))';
    return 'hsl(var(--color-text-secondary))';
  };
  
  // Get debug info for a channel
  const getChannelDebug = (channelId) => {
    if (!recommendationDebug) return null;
    return recommendationDebug[channelId];
  };
  
  return (
    <GlassCard className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, hsl(var(--color-primary)), hsl(280 60% 55%))' 
            }}
          >
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-semantic">Canaux recommandes</h3>
            <p className="text-xs text-semantic-muted">Scoring IA v2.0 - Max 95/100</p>
          </div>
        </div>
        <span 
          className="text-xs font-medium px-2 py-1 rounded-full"
          style={{ 
            backgroundColor: 'hsl(var(--color-primary) / 0.1)',
            color: 'hsl(var(--color-primary))'
          }}
        >
          IA
        </span>
      </div>
      
      {/* Analysis Summary - NEW */}
      {analysisSummary && (
        <div 
          className="p-3 rounded-xl mb-4 text-xs"
          style={{ 
            backgroundColor: 'hsl(var(--color-surface-2))',
            border: '1px solid hsl(var(--color-border-subtle))'
          }}
        >
          <div className="font-medium text-semantic mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: 'hsl(var(--color-primary))' }} />
            Analyse detectee
          </div>
          <div className="grid grid-cols-2 gap-2 text-semantic-muted">
            {analysisSummary.detected_sector && (
              <div>
                <span className="opacity-70">Secteur : </span>
                <span className="text-semantic font-medium capitalize">{analysisSummary.detected_sector}</span>
              </div>
            )}
            {analysisSummary.detected_audience && (
              <div>
                <span className="opacity-70">Audience : </span>
                <span className="text-semantic font-medium uppercase">{analysisSummary.detected_audience}</span>
              </div>
            )}
            {analysisSummary.detected_goal && (
              <div>
                <span className="opacity-70">Objectif : </span>
                <span className="text-semantic font-medium capitalize">{analysisSummary.detected_goal}</span>
              </div>
            )}
            {analysisSummary.content_characteristics && Object.keys(analysisSummary.content_characteristics).length > 0 && (
              <div className="col-span-2">
                <span className="opacity-70">Contenu : </span>
                <span className="text-semantic font-medium">
                  {Object.keys(analysisSummary.content_characteristics)
                    .filter(k => analysisSummary.content_characteristics[k])
                    .slice(0, 3)
                    .map(k => k.replace(/_/g, ' '))
                    .join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Recommendations List */}
      <div className="space-y-3 mb-5">
        {recommendations.slice(0, 7).map((rec, index) => {
          const debug = getChannelDebug(rec.channel);
          const isExpanded = expandedChannel === rec.channel;
          
          return (
            <div key={rec.channel}>
              {/* Main Row */}
              <div 
                className={cn(
                  "flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer",
                  index < 3 
                    ? "bg-gradient-to-r from-semantic-surface-2 to-transparent border border-semantic-border"
                    : "bg-semantic-surface-2/50",
                  isExpanded && "rounded-b-none"
                )}
                style={index === 0 ? { 
                  borderColor: 'hsl(var(--color-success) / 0.3)',
                  background: 'linear-gradient(90deg, hsl(var(--color-success) / 0.05), transparent)'
                } : undefined}
                onClick={() => toggleExpanded(rec.channel)}
                data-testid={`channel-row-${rec.channel}`}
              >
                {/* Rank */}
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ 
                    backgroundColor: index === 0 ? 'hsl(var(--color-success))' : 
                                    index < 3 ? 'hsl(var(--color-primary))' : 
                                    'hsl(var(--color-surface-3))',
                    color: index < 3 ? 'white' : 'hsl(var(--color-text-secondary))'
                  }}
                >
                  {index + 1}
                </div>
                
                {/* Channel Icon */}
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: rec.color + '15', color: rec.color }}
                >
                  {ChannelLogos[rec.channel] || <Globe className="w-5 h-5" />}
                </div>
                
                {/* Channel Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-semantic text-sm">{rec.name}</span>
                    <span 
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={getBadgeStyle(rec.level)}
                    >
                      {rec.badge}
                    </span>
                  </div>
                  <p className="text-xs text-semantic-muted truncate">
                    {rec.reasons?.slice(0, 2).join(' - ') || rec.description}
                  </p>
                </div>
                
                {/* Score + Expand */}
                <div className="text-right flex-shrink-0 flex items-center gap-2">
                  <div>
                    <div 
                      className="text-lg font-bold"
                      style={{ color: getScoreColor(rec.score) }}
                    >
                      {rec.score}
                    </div>
                    <div className="text-[10px] text-semantic-muted">/95</div>
                  </div>
                  <ChevronRight 
                    className={cn(
                      "w-4 h-4 text-semantic-muted transition-transform",
                      isExpanded && "rotate-90"
                    )} 
                  />
                </div>
              </div>
              
              {/* Expanded Debug Panel - NEW */}
              {isExpanded && debug && (
                <div 
                  className="p-4 rounded-b-xl text-xs"
                  style={{ 
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    borderTop: 'none',
                    border: '1px solid hsl(var(--color-border-subtle))',
                    borderTopWidth: 0
                  }}
                  data-testid={`channel-debug-${rec.channel}`}
                >
                  <div className="font-semibold text-semantic mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-primary))' }} />
                    Pourquoi ce score ?
                  </div>
                  
                  {/* Score Breakdown */}
                  <div className="space-y-1.5 mb-3">
                    <div className="flex justify-between text-semantic-muted">
                      <span>Score de base</span>
                      <span className="font-mono text-semantic">+{debug.base_score || 0}</span>
                    </div>
                    
                    {debug.bonuses && debug.bonuses.map((bonus, i) => {
                      const isPositive = bonus.startsWith('+');
                      return (
                        <div key={i} className="flex justify-between text-semantic-muted">
                          <span className="truncate mr-2">{bonus.replace(/^[+-]\d+\s*/, '')}</span>
                          <span 
                            className="font-mono flex-shrink-0"
                            style={{ 
                              color: isPositive 
                                ? 'hsl(var(--color-success))' 
                                : 'hsl(var(--color-error))'
                            }}
                          >
                            {bonus.match(/^[+-]\d+/)?.[0] || ''}
                          </span>
                        </div>
                      );
                    })}
                    
                    {debug.cross_channel_adjustment && (
                      <div className="flex justify-between text-semantic-muted border-t border-semantic-border pt-1.5 mt-1.5">
                        <span>Ajustement cross-canal</span>
                        <span className="font-mono" style={{ color: 'hsl(var(--color-warning))' }}>
                          {debug.cross_channel_adjustment}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between border-t border-semantic-border pt-1.5 mt-1.5">
                      <span className="font-semibold text-semantic">Total</span>
                      <span 
                        className="font-mono font-bold"
                        style={{ color: getScoreColor(rec.score) }}
                      >
                        {rec.score}/95
                      </span>
                    </div>
                  </div>
                  
                  {/* Matched Info */}
                  {debug.matched_sectors && debug.matched_sectors.length > 0 && (
                    <div className="text-semantic-muted">
                      <span className="opacity-70">Secteurs detectes : </span>
                      <span className="text-semantic">{debug.matched_sectors.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend - Updated thresholds */}
      <div className="flex items-center gap-4 mb-4 text-xs text-semantic-muted">
        <div className="flex items-center gap-1.5">
          <Star className="w-3 h-3" style={{ color: 'hsl(var(--color-success))' }} />
          <span>Tres recommande (75+)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Star className="w-3 h-3" style={{ color: 'hsl(var(--color-primary))' }} />
          <span>Recommande (55+)</span>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleApplyRecommendations}
          disabled={applying}
          className={cn(
            "flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all",
            "flex items-center justify-center gap-2",
            "bg-gradient-to-r from-[hsl(var(--color-primary))] to-[hsl(280_60%_55%)]",
            "text-white hover:opacity-90",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          data-testid="apply-channel-recommendations-btn"
        >
          {applying ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Appliquer
            </>
          )}
        </button>
        
        <button
          onClick={handleResetOrder}
          disabled={resetting}
          className={cn(
            "py-3 px-4 rounded-xl font-medium text-sm transition-all",
            "flex items-center justify-center gap-2",
            "bg-semantic-surface-2 text-semantic-muted hover:text-semantic",
            "border border-semantic-border",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          title="Reinitialiser l'ordre des canaux"
          data-testid="reset-channel-order-btn"
        >
          {resetting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ArrowRight className="w-4 h-4 rotate-180" />
          )}
        </button>
      </div>
      
      <p className="text-[11px] text-semantic-muted text-center mt-2">
        Cliquez sur un canal pour voir le detail du calcul
      </p>
    </GlassCard>
  );
}

/**
 * Step 1: Analyser mon site
 * =========================
 * iOS 26-inspired design
 * USES SEMANTIC TOKENS for dark mode compatibility
 * 
 * NEW: Includes inline project editing when URL is missing
 */
function AnalyzeStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { completeStep, stepStatuses } = useProjectSteps();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // NEW: Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    website_url: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    loadProject();
  }, [projectId]);
  
  const loadProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
      setEditForm({
        name: response.data.name || '',
        website_url: response.data.website_url || '',
        description: response.data.description || ''
      });
      
      // Check if already analyzed
      if (response.data.website_analyzed && response.data.analysis_result) {
        setAnalysisResult(response.data.analysis_result);
      }
      
      // Auto-open edit mode if no URL
      if (!response.data.website_url) {
        setIsEditing(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // NEW: Save project changes
  const handleSaveProject = async () => {
    if (!editForm.name.trim()) {
      toast.error('Le nom du projet est requis');
      return;
    }
    if (!editForm.website_url.trim()) {
      toast.error('L\'URL du site est requise pour l\'analyse');
      return;
    }
    
    // Validate URL format
    try {
      new URL(editForm.website_url);
    } catch {
      toast.error('L\'URL n\'est pas valide. Exemple: https://mon-site.com');
      return;
    }
    
    setSaving(true);
    try {
      const response = await apiClient.put(`/projects/${projectId}`, {
        name: editForm.name.trim(),
        website_url: editForm.website_url.trim(),
        description: editForm.description.trim()
      });
      setProject(response.data);
      setIsEditing(false);
      toast.success('Projet mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAnalyze = async () => {
    if (!project?.website_url) {
      toast.error('Aucune URL de site configurée');
      setIsEditing(true); // Open edit form
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/analyze`);
      setAnalysisResult(response.data);
      toast.success('Analyse terminée !');
      completeStep('analyze');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const handleContinue = () => {
    navigate(`/projects/${projectId}/setup/connect`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(var(--color-primary))' }} />
      </div>
    );
  }
  
  const isCompleted = stepStatuses.analyze === 'completed' || analysisResult;
  const hasUrl = !!project?.website_url;
  
  return (
    <div className="max-w-3xl mx-auto">
      <SectionHeader
        title="Analyser mon site"
        subtitle="L'IA analyse votre contenu pour comprendre votre activité"
      />

      {/* iter 241 Lot 4 — engine picker + engine-scoped SEO status.
          Hidden if Multimoteur V2 is OFF (legacy fallback). */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <EngineSelector projectId={projectId} />
        <div className="text-xs text-semantic-muted">
          Chaque moteur possède sa propre analyse SEO
        </div>
      </div>
      <EngineSeoPanel projectId={projectId} />
      
      {/* Warning if no URL */}
      {!hasUrl && !isEditing && (
        <div 
          className="flex items-center gap-3 p-4 rounded-xl mb-6"
          style={{ 
            backgroundColor: 'hsl(var(--color-warning-bg))',
            border: '1px solid hsl(var(--color-warning) / 0.3)'
          }}
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(var(--color-warning))' }} />
          <div className="flex-1">
            <p className="font-medium" style={{ color: 'hsl(var(--color-warning-text))' }}>
              Aucune URL de site configurée
            </p>
            <p className="text-sm" style={{ color: 'hsl(var(--color-warning-text))' }}>
              Ajoutez l&apos;URL de votre site pour lancer l&apos;analyse IA
            </p>
          </div>
          <PillButton
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Configurer
          </PillButton>
        </div>
      )}
      
      {/* Project Info Card - View Mode or Edit Mode */}
      <GlassCard className="p-6 mb-8">
        {isEditing ? (
          /* ==================== EDIT MODE ==================== */
          <div className="space-y-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-semantic flex items-center gap-2">
                <Edit3 className="w-5 h-5" style={{ color: 'hsl(var(--color-primary))' }} />
                Configurer le projet
              </h3>
              {hasUrl && (
                <button
                  onClick={() => setIsEditing(false)}
                  className="text-sm text-semantic-muted hover:text-semantic transition-colors"
                >
                  Annuler
                </button>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-semantic mb-2">
                Nom du projet *
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Mon site e-commerce"
                className="w-full input-semantic"
                data-testid="project-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-semantic mb-2">
                URL du site web * 
                <span className="font-normal text-semantic-muted ml-1">(obligatoire pour l&apos;analyse)</span>
              </label>
              <div className="relative">
                <Globe 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: 'hsl(var(--color-text-muted))' }}
                />
                <input
                  type="url"
                  value={editForm.website_url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://mon-site.com"
                  className="w-full input-semantic"
                  style={{ paddingLeft: '2.75rem' }}
                  data-testid="project-url-input"
                />
              </div>
              <p className="text-xs text-semantic-muted mt-1">
                L&apos;IA analysera cette URL pour comprendre votre activité
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-semantic mb-2">
                Description <span className="font-normal text-semantic-muted">(optionnel)</span>
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez brièvement votre activité..."
                rows={3}
                className="w-full input-semantic resize-none"
                data-testid="project-description-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <PillButton
                variant="primary"
                className="flex-1"
                onClick={handleSaveProject}
                loading={saving}
                data-testid="save-project-btn"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer et continuer'}
              </PillButton>
            </div>
          </div>
        ) : (
          /* ==================== VIEW MODE ==================== */
          <>
            <div className="flex items-center gap-4 mb-6">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
              >
                <Globe className="w-7 h-7" style={{ color: 'hsl(var(--color-primary))' }} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-semantic">{project?.name || 'Mon site'}</h3>
                {hasUrl ? (
                  <a 
                    href={project.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm flex items-center gap-1 hover:underline"
                    style={{ color: 'hsl(var(--color-primary))' }}
                  >
                    {project.website_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <p className="text-sm text-semantic-muted">Aucune URL configurée</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isCompleted ? (
                  <StatusChip status="success">Analysé</StatusChip>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                    title="Modifier le projet"
                    data-testid="edit-project-btn"
                  >
                    <Edit3 className="w-4 h-4 text-semantic-muted" />
                  </button>
                )}
              </div>
            </div>
            
            {!isCompleted && hasUrl && (
              <>
                <p className="text-semantic-2 mb-6">
                  L&apos;analyse prend environ 30 secondes. L&apos;IA va :
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Détecter la langue de votre site',
                    'Identifier vos thèmes et sujets clés',
                    'Analyser votre positionnement SEO',
                    'Proposer des idées de contenu'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-semantic-2">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
                      >
                        <Sparkles className="w-3 h-3" style={{ color: 'hsl(var(--color-primary))' }} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                
                <PillButton
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={handleAnalyze}
                  loading={analyzing}
                  icon={<Search className="w-5 h-5" />}
                  data-testid="analyze-btn"
                >
                  {analyzing ? 'Analyse en cours...' : 'Analyser mon site maintenant'}
                </PillButton>
              </>
            )}
            
            {!isCompleted && !hasUrl && (
              <div className="text-center py-4">
                <p className="text-semantic-muted mb-4">
                  Configurez l&apos;URL de votre site pour lancer l&apos;analyse
                </p>
                <PillButton
                  variant="primary"
                  onClick={() => setIsEditing(true)}
                  icon={<Edit3 className="w-4 h-4" />}
                  data-testid="configure-url-btn"
                >
                  Configurer l&apos;URL du site
                </PillButton>
              </div>
            )}
          </>
        )}
      </GlassCard>
      
      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-6 ios-animate-fade-in">
          <h3 className="text-lg font-semibold text-semantic">Résultats de l&apos;analyse</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Language */}
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(270 50% 92%)' }}
                >
                  <Languages className="w-5 h-5" style={{ color: 'hsl(270 60% 55%)' }} />
                </div>
                <span className="font-medium text-semantic-2">Langue détectée</span>
              </div>
              <p className="text-2xl font-bold text-semantic">
                {analysisResult.language === 'fr' ? '🇫🇷 Français' : 
                 analysisResult.language === 'en' ? '🇬🇧 Anglais' : 
                 analysisResult.language || 'Non détectée'}
              </p>
            </GlassCard>
            
            {/* SEO Score */}
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(var(--color-success-bg))' }}
                >
                  <TrendingUp className="w-5 h-5" style={{ color: 'hsl(var(--color-success))' }} />
                </div>
                <span className="font-medium text-semantic-2">Score SEO</span>
              </div>
              <div className="flex items-end gap-2">
                <p className="text-2xl font-bold text-semantic">
                  {analysisResult.seo_score || 75}/100
                </p>
                <span 
                  className="text-sm font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: (analysisResult.seo_score || 75) >= 70 
                      ? 'hsl(var(--color-success-bg))' 
                      : 'hsl(var(--color-warning-bg))',
                    color: (analysisResult.seo_score || 75) >= 70 
                      ? 'hsl(var(--color-success-text))' 
                      : 'hsl(var(--color-warning-text))'
                  }}
                >
                  {(analysisResult.seo_score || 75) >= 70 ? 'Bon' : 'À améliorer'}
                </span>
              </div>
            </GlassCard>
          </div>
          
          {/* ====================================================
              P0 FIX: Theme Validation Editor (Legacy Feature Parity)
              ====================================================
              Intégration du composant complet de validation des thèmes
              avec édition, re-analyse et validation comme dans ProjectSettings
          */}
          <GlassCard className="p-5">
            <ThemeValidationEditor
              projectId={projectId}
              themes={project?.extracted_themes || analysisResult}
              onThemesUpdate={(updatedThemes) => {
                setProject(prev => ({
                  ...prev,
                  extracted_themes: updatedThemes
                }));
                // Mark step as completed when themes are validated
                if (updatedThemes.validated) {
                  completeStep('analyze');
                }
              }}
              compact={true}
            />
          </GlassCard>
          
          {/* Validation Recommendation Banner */}
          {project?.extracted_themes && !project.extracted_themes.validated && (
            <div 
              className="flex items-center gap-3 p-4 rounded-xl"
              style={{ 
                backgroundColor: 'hsl(var(--color-info-bg))',
                border: '1px solid hsl(var(--color-primary) / 0.3)'
              }}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(var(--color-primary))' }} />
              <div className="flex-1">
                <p className="font-medium text-semantic text-sm">
                  Validation recommandée
                </p>
                <p className="text-xs text-semantic-muted">
                  Vérifiez et validez les thèmes avant de passer à l&apos;étape suivante pour de meilleurs résultats.
                </p>
              </div>
            </div>
          )}
          
          {/* Content Ideas */}
          {analysisResult.content_ideas && analysisResult.content_ideas.length > 0 && (
            <GlassCard className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(var(--color-warning-bg))' }}
                >
                  <Sparkles className="w-5 h-5" style={{ color: 'hsl(var(--color-warning))' }} />
                </div>
                <div>
                  <span className="font-medium text-semantic">Idées de contenu</span>
                  <p className="text-xs text-semantic-muted">Suggestions basées sur votre analyse</p>
                </div>
              </div>
              <ul className="space-y-2">
                {analysisResult.content_ideas.slice(0, 5).map((idea, i) => (
                  <li key={i} className="flex items-start gap-2 text-semantic-2 text-sm">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(var(--color-success))' }} />
                    {idea}
                  </li>
                ))}
              </ul>
            </GlassCard>
          )}
          
          {/* P1 NEW: Channel Recommendations */}
          {analysisResult.channel_recommendations && analysisResult.channel_recommendations.length > 0 && (
            <ChannelRecommendations 
              recommendations={analysisResult.channel_recommendations}
              projectId={projectId}
              analysisSummary={analysisResult.analysis_summary}
              recommendationDebug={analysisResult.recommendation_debug}
              onApply={() => {
                toast.success('Les canaux recommandes seront mis en avant dans l\'etape suivante');
              }}
            />
          )}
          
          {/* Continue Button */}
          <PillButton
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleContinue}
            icon={<ArrowRight className="w-5 h-5" />}
            iconPosition="right"
            data-testid="continue-to-connect-btn"
          >
            Continuer vers les connexions
          </PillButton>
        </div>
      )}
    </div>
  );
}

/**
 * Step 1 Page Wrapper
 */
export default function AnalyzeStep() {
  return (
    <GuidedProjectLayout>
      <AnalyzeStepContent />
    </GuidedProjectLayout>
  );
}
